/**
 * @module Notifications
 *
 * Stores short-term messages from the application for the user to review. They
 * can be one of multiple types (e.g., "error", "info", "success", etc.). The
 * user can dismiss individual (or all) notifications, or later review a list
 * of un-dismissed notifications. This state is temporary and only meant to
 * represent messages that the user has not yet seen/reviewed to ensure that
 * they have an opportunity to see important app messages.
 *
 * @example notifications
 * {
 *   'lR3mwYjlTh': {
 *     id: 'lR3mwYjlTh',
 *     title: 'Something important happened!',
 *     message: 'This is a detailed explanation of all the important things you should know about.',
 *     status: 'warning',
 *     isVisible: true
 *   }
 * }
 */
import { createReducer, nanoid } from '@reduxjs/toolkit';
import { userActions } from '../actions/user.actions';
import { trendsActions } from '../actions/trends.actions';
import { taskActions } from '../actions/task.actions';
import { surveyTypeActions } from '../actions/survey_type.actions';
import { taskTemplateActions } from '../actions/task_template.actions';
import { surveyReminderActions } from '../actions/survey_reminder.actions';
import { surveyElementActions } from '../actions/survey_element.action';
import { surveyActions } from '../actions/survey.actions';
import { reportingActions } from '../actions/reporting.actions';
import { questionLogicActions } from '../actions/question_logic.actions';
import { notificationActions } from '../actions/notification.actions';
import { metaActions } from '../actions/meta.actions';
import { employeeAttributeActions } from '../actions/employee_attribute.actions';
import { employeeActions } from '../actions/employee.actions';
import { companyActions } from '../actions/company.actions';
import { categoryActions } from '../actions/category.actions';
import * as languages from '../languages';
import * as notificationSettings from '~/config/settings/notification.settings';

const defaultState = {};

const reset = () => defaultState;

const isValidNotification = (notification) => {
  return !!notification?.title && notificationSettings.statusList.includes(notification?.status);
};

const addNotification =
  ({ forceError = false } = {}) =>
  (state, action) => {
    // Check if notification was sent directly or if it is encapsulated within
    // its own namespaced object.
    // We need to check for `error` and `errors` as not all requests can be handled by rejectWithValue from Redux,
    // and we are also moving away from it.
    const payload = action?.payload?.notification ?? action.payload ?? action?.error ?? action?.errors?.[0] ?? {};

    // There's no easy way to know if a given action is an error or not as it
    // might be auto-generated and not contain a `status` attribute. The `forceError`
    // param can be used to explicitly tell the function to create an error
    // notification regardless of the content of the action. You can simply omit
    // the `forceError` param and the default behavior is to use the values set
    // in the action's payload.
    if (forceError) {
      if (!payload.title) payload.title = languages.t('error.somethingWentWrong') || 'Error';
      payload.status = notificationSettings.statuses.error;
    }

    const notification = {
      id: nanoid(10),
      title: payload?.title,
      message: payload?.message,
      status: payload?.status,
      isVisible: true,
    };

    if (!isValidNotification(notification)) return state;

    state[notification.id] = {
      ...state[notification.id],
      ...notification,
    };
  };

const displayNotifications = (state, action) => {
  state = state.map((notification) => ({
    ...notification,
    isVisible: true,
  }));
  return state;
};

const hideNotification = (state, action) => {
  const notificationId = action?.payload?.id;

  if (notificationId) {
    const { [notificationId]: dismissedNotification, ...remainingNotifications } = state;

    return remainingNotifications;
  }
};

const notifications = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(notificationActions.createNotification, addNotification())
    .addCase(notificationActions.showNotifications, displayNotifications)
    .addCase(notificationActions.dismissNotification, hideNotification)
    .addCase(surveyTypeActions.updateSurveyType.rejected, addNotification({ forceError: true }))
    .addCase(surveyTypeActions.updateSurveyType.fulfilled, addNotification())
    .addCase(surveyTypeActions.createSurveyType.rejected, addNotification({ forceError: true }))
    .addCase(surveyTypeActions.createSurveyType.fulfilled, addNotification())
    .addCase(categoryActions.createCategory.rejected, addNotification({ forceError: true }))
    .addCase(categoryActions.createCategory.fulfilled, addNotification())
    .addCase(categoryActions.updateCategory.rejected, addNotification({ forceError: true }))
    .addCase(categoryActions.updateCategory.fulfilled, addNotification())
    .addCase(categoryActions.removeCategory.rejected, addNotification({ forceError: true }))
    .addCase(categoryActions.removeCategory.fulfilled, addNotification())
    .addCase(taskTemplateActions.removeTaskTemplate.rejected, addNotification({ forceError: true }))
    .addCase(taskTemplateActions.removeTaskTemplate.fulfilled, addNotification())
    .addCase(taskTemplateActions.modifyTaskTemplate.rejected, addNotification({ forceError: true }))
    .addCase(taskTemplateActions.modifyTaskTemplate.fulfilled, addNotification())
    .addCase(taskTemplateActions.createTaskTemplate.rejected, addNotification({ forceError: true }))
    .addCase(taskTemplateActions.createTaskTemplate.fulfilled, addNotification())
    .addCase(taskActions.removeTask.rejected, addNotification({ forceError: true }))
    .addCase(taskActions.removeTask.fulfilled, addNotification())
    .addCase(taskActions.modifyTask.rejected, addNotification({ forceError: true }))
    .addCase(taskActions.modifyTask.fulfilled, addNotification())
    .addCase(taskActions.createTask.rejected, addNotification({ forceError: true }))
    .addCase(taskActions.createTask.fulfilled, addNotification())
    .addCase(companyActions.modifyCompany.rejected, addNotification({ forceError: true }))
    .addCase(companyActions.modifyCompany.fulfilled, addNotification())
    .addCase(companyActions.deleteCompany.rejected, addNotification({ forceError: true }))
    .addCase(companyActions.deleteCompany.fulfilled, addNotification())
    .addCase(userActions.changeProfile.fulfilled, addNotification())
    .addCase(userActions.changeProfile.rejected, addNotification({ forceError: true }))
    .addCase(employeeActions.createEmployee.fulfilled, addNotification())
    .addCase(employeeActions.createEmployee.rejected, addNotification({ forceError: true }))
    .addCase(employeeActions.modifyEmployee.fulfilled, addNotification())
    .addCase(employeeActions.modifyEmployee.rejected, addNotification({ forceError: true }))
    .addCase(employeeActions.exportEmployees.fulfilled, addNotification())
    .addCase(employeeActions.exportEmployees.rejected, addNotification({ forceError: true }))
    .addCase(employeeActions.importEmployees.fulfilled, addNotification())
    .addCase(employeeActions.importEmployees.rejected, addNotification({ forceError: true }))
    .addCase(employeeActions.updateEmployeeIds.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.createSurvey.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.createSurvey.fulfilled, addNotification())
    .addCase(surveyActions.updateSurvey.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.updateSurvey.fulfilled, addNotification())
    .addCase(surveyActions.sendTestSurvey.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.sendTestSurvey.fulfilled, addNotification())
    .addCase(surveyActions.cancelScheduleSurvey.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.cancelScheduleSurvey.fulfilled, addNotification())
    .addCase(surveyElementActions.getLibraryQuestions.rejected, addNotification({ forceError: true }))
    .addCase(surveyElementActions.getTrendQuestions.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.fetchSurveyWithSurveyElements.rejected, addNotification({ forceError: true }))
    .addCase(employeeAttributeActions.fetchSurveyTypeAttributes.rejected, addNotification({ forceError: true }))
    .addCase(surveyElementActions.createSurveyElement.rejected, addNotification({ forceError: true }))
    .addCase(surveyElementActions.updateSurveyElement.rejected, addNotification({ forceError: true }))
    .addCase(surveyElementActions.deleteSurveyElement.rejected, addNotification({ forceError: true }))
    .addCase(questionLogicActions.getQuestionLogics.rejected, addNotification({ forceError: true }))
    .addCase(questionLogicActions.createQuestionLogic.rejected, addNotification({ forceError: true }))
    .addCase(questionLogicActions.updateQuestionLogic.rejected, addNotification({ forceError: true }))
    .addCase(questionLogicActions.deleteQuestionLogic.rejected, addNotification({ forceError: true }))
    .addCase(reportingActions.fetchQuestionResults.rejected, addNotification({ forceError: true }))
    .addCase(reportingActions.fetchQuestionAttributeResponses.rejected, addNotification({ forceError: true }))
    .addCase(trendsActions.fetchTrendResults.rejected, addNotification({ forceError: true }))
    .addCase(reportingActions.fetchFullResponses.rejected, addNotification({ forceError: true }))
    .addCase(userActions.createRole.rejected, addNotification({ forceError: true }))
    .addCase(userActions.updateRole.rejected, addNotification({ forceError: true }))
    .addCase(userActions.deleteRole.fulfilled, addNotification())
    .addCase(userActions.deleteRole.rejected, addNotification({ forceError: true }))
    .addCase(userActions.modifyUserPermissions.rejected, addNotification({ forceError: true }))
    .addCase(userActions.createUsers.rejected, addNotification({ forceError: true }))
    .addCase(userActions.resendUserInvites.fulfilled, addNotification())
    .addCase(userActions.resendUserInvites.rejected, addNotification({ forceError: true }))
    .addCase(userActions.deletePendingUsers.fulfilled, addNotification())
    .addCase(userActions.deletePendingUsers.rejected, addNotification({ forceError: true }))
    .addCase(userActions.activateUsers.fulfilled, addNotification())
    .addCase(userActions.activateUsers.rejected, addNotification({ forceError: true }))
    .addCase(userActions.deactivateUsers.fulfilled, addNotification())
    .addCase(userActions.deactivateUsers.rejected, addNotification({ forceError: true }))
    .addCase(userActions.exitImpersonation.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.duplicateSurvey.rejected, addNotification({ forceError: true }))
    .addCase(surveyReminderActions.upsertReminder.fulfilled, addNotification())
    .addCase(surveyReminderActions.upsertReminder.rejected, addNotification({ forceError: true }))
    .addCase(surveyReminderActions.deleteReminder.fulfilled, addNotification())
    .addCase(surveyReminderActions.deleteReminder.rejected, addNotification({ forceError: true }))
    .addCase(surveyActions.migrateSurvey.fulfilled, addNotification());
});

export default notifications;
